

import { useState, useEffect, useMemo, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import ConfirmationModal from "../components/ConfirmationModal";
import SearchInput from "../components/SearchInput";
import DataTable from "../components/DataTable";
import FullScreenQuotationModal from "./FullScreenQuotationModalRefactored";
import StatusUpdatePopup from "./StatusUpdatePopup";
import ConvertToEventModal, { ConvertEventData } from "./ConvertToEventModal";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/context/ToastContext";
import { useSettings } from "@/context/SettingsContext";
import { quotationsApi, mastersApi } from "@/lib/api";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";
import PrintableQuotation from "./components/PrintableQuotation";

const statuses = ["all", "draft", "sent", "approved", "rejected", "expired"] as const;
type Status = typeof statuses[number];

interface QuotationItem {
  id?: number;
  quotation_id?: number;
  service_id?: number;
  service?: { id: number; name: string; selling_price: number };
  service_name?: string;
  vendor_id?: number;
  vendor?: { id: number; name: string };
  vendor_name?: string;
  description?: string;
  quantity: number;
  unit_price: number;
  cost_price?: number;
  notes?: string;
}

interface VendorItem {
  id?: number;
  vendor_id?: number;
  description?: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  cost_price?: number;
  commission_percent?: number;
  commission_type?: string;
  notes?: string;
}

interface QuotationFormData {
  id?: number;
  quotation_number?: string;
  client_id: number;
  client_name: string;
  event_name: string;
  event_type: string;
  guests: string;
  event_date: string;
  package_id?: number;
  venue: string;
  venue_id?: number;
  services_data: QuotationItem[];
  vendors_data: VendorItem[];
  discount_type: 'fixed' | 'percentage';
  discount_value: string;
  tax: string;
  is_taxable: boolean;
  notes: string;
  valid_until: string;
  status: string;
  subtotal?: number;
  total_amount?: number;
}

interface Quotation {
  id: number;
  slug?: string;
  quotation_number: string;
  client_id: number;
  client?: { id: number; name: string };
  enquiry_id?: number;
  event_type: string;
  event_name?: string;
  name?: string;
  guests: number;
  event_date: string;
  package_id?: number;
  package?: { id: number; name: string };
  venue: string;
  venue_id?: number;
  items?: QuotationItem[];
  quotation_services?: QuotationItem[];
  vendors_data?: any[];
  quotation_vendors?: any[];
  subtotal: number;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  tax: number;
  total: number;
  status: string;
  valid_until: string;
  notes: string;
}

function QuotationsContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { settings } = useSettings();
  const logoUrl = settings?.logoUrl ? getImageUrl(settings.logoUrl) : null;
  const businessName = settings?.business?.businessName || 'Shalimar';
  const { showToast } = useToast();
  const isStaff = user?.role === 'staff';
  
  // Check for edit parameter in URL
  const editQuotationId = searchParams.get("edit");
  
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<Status>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState<Partial<QuotationFormData> | null>(null);
  const [showStats, setShowStats] = useState(false);

  // Convert to event modal state
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [convertingQuotation, setConvertingQuotation] = useState<Quotation | null>(null);
  const [isConvertLoading, setIsConvertLoading] = useState(false);
  const [convertError, setConvertError] = useState("");

  // Status update popup state
  const [isStatusPopupOpen, setIsStatusPopupOpen] = useState(false);
  const [statusUpdateQuotation, setStatusUpdateQuotation] = useState<Quotation | null>(null);

  // Delete confirmation modal state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deletingQuotation, setDeletingQuotation] = useState<Quotation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloning, setIsCloning] = useState<number | null>(null);

  // Status change confirmation state
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [statusConfirmQuotation, setStatusConfirmQuotation] = useState<Quotation | null>(null);
  const [statusConfirmNext, setStatusConfirmNext] = useState<string>("");
  const [printQuotation, setPrintQuotation] = useState<Quotation | null>(null);

  // Fetch quotations from API
  useEffect(() => {
    loadQuotations();
  }, []);

  // Handle edit parameter from URL
  useEffect(() => {
    if (editQuotationId && quotations.length > 0) {
      const quotationToEdit = quotations.find(q => q.id === parseInt(editQuotationId));
      if (quotationToEdit) {
        const transformedQuotation = transformQuotationForEdit(quotationToEdit);
        setEditingQuotation(transformedQuotation);
        setIsModalOpen(true);
        // Clear the edit parameter from URL
        navigate('/quotations', { replace: true });
      }
    }
  }, [editQuotationId, quotations, navigate]);

  // Handle print events
  useEffect(() => {
    const handleAfterPrint = () => {
      if (printQuotation) {
        showToast("Quotation ready - use browser's save as PDF option", "success");
      }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [printQuotation, showToast]);

  const loadQuotations = async () => {
    try {
      setLoading(true);
      const response = await quotationsApi.list();
      let quotationsData = response.data?.data || response.data || [];
      
      // Transform quotations to include services and vendors from relationships
      quotationsData = quotationsData.map((q: any) => ({
        ...q,
        items: q.quotation_services || q.items || [],
        vendors_data: q.quotation_vendors || q.vendors_data || [],
      }));
      
      setQuotations(quotationsData);
    } catch (error) {
      console.error("Error loading quotations:", error);
    } finally {
      setLoading(false);
    }
  };

  const statusCounts = useMemo(() => {
    const counts: Record<Status, number> = { 
      all: quotations.length, 
      draft: quotations.filter(q => q.status === 'draft').length, 
      sent: quotations.filter(q => q.status === 'sent').length, 
      approved: quotations.filter(q => q.status === 'approved').length, 
      rejected: quotations.filter(q => q.status === 'rejected').length, 
      expired: quotations.filter(q => q.status === 'expired').length 
    };
    return counts;
  }, [quotations]);

  const filtered = useMemo(() => {
    return quotations.filter(q => {
      const matchStatus = activeFilter === "all" || q.status === activeFilter;
      const qry = searchQuery.toLowerCase();
      const clientName = q.client?.name || "";
      const matchSearch = !qry || 
        clientName.toLowerCase().includes(qry) || 
        q.quotation_number?.toLowerCase().includes(qry) ||
        q.event_type?.toLowerCase().includes(qry);
      return matchStatus && matchSearch;
    });
  }, [activeFilter, searchQuery, quotations]);

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this quotation?")) return;
    try {
      await quotationsApi.delete(id);
      loadQuotations();
    } catch (error) {
      console.error("Error deleting quotation:", error);
    }
  };

  const handleOpenConvertModal = (quotation: Quotation) => {
    setConvertingQuotation(quotation);
    setConvertError("");
    setIsConvertModalOpen(true);
  };

  const handleConfirmConvert = async (data: ConvertEventData) => {
    try {
      setIsConvertLoading(true);
      setConvertError("");
      
      const convertPayload = {
        quotation_id: data.quotation_id,
        event_date: data.event_date,
        advance_payment: data.advance_payment,
        payment_method: data.payment_method,
        payment_date: data.payment_date,
        payment_proof: data.payment_proof,
      };
      
      await quotationsApi.convertToEvent(data.quotation_id, convertPayload);
      
      setIsConvertModalOpen(false);
      setConvertingQuotation(null);
      loadQuotations();
    } catch (error: any) {
      setConvertError(error?.message || "Failed to convert quotation to event");
      throw error;
    } finally {
      setIsConvertLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      // Calculate subtotal and total
      const servicesTotal = data.services_data?.reduce((sum: number, item: any) => sum + (item.quantity * item.unit_price), 0) || 0;
      const vendorsTotal = data.vendors_data?.reduce((sum: number, item: any) => sum + (item.quantity * item.unit_price), 0) || 0;
      const subtotal = servicesTotal + vendorsTotal;
      
      const discountValue = parseFloat(data.discount_value) || 0;
      const discount = data.discount_type === 'percentage' 
        ? subtotal * (discountValue / 100) 
        : discountValue;
      const afterDiscount = Math.max(0, subtotal - discount);
      const taxPercent = parseFloat(data.tax) || 0;
      const taxAmount = afterDiscount * (taxPercent / 100);
      const total = afterDiscount + taxAmount;

      // Prepare data for API - ensure proper formatting
      const quotationName = data.event_name || `${data.client_name} - ${data.quotation_number}` || data.quotation_number || "Quotation";
      
      const payload = {
        name: quotationName,
        quotation_number: data.quotation_number,
        client_id: data.client_id,
        client_name: data.client_name,
        event_name: data.event_name,
        event_type: data.event_type,
        guests: parseInt(data.guests) || 0,
        event_date: data.event_date,
        venue: data.venue || null,
        venue_id: data.venue_id || null,
        package_id: data.package_id || null,
        items: data.services_data?.map((item: any) => ({
          service_id: item.service_id || null,
          description: item.description || "",
          quantity: item.quantity || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          cost_price: parseFloat(item.cost_price) || 0,
          notes: item.notes || "",
        })) || [],
        vendors: data.vendors_data?.map((vendor: any) => ({
          vendor_id: vendor.vendor_id || null,
          description: vendor.description || "",
          quantity: vendor.quantity || 1,
          unit: vendor.unit || "unit",
          unit_price: parseFloat(vendor.unit_price) || 0,
          cost_price: parseFloat(vendor.cost_price) || 0,
          commission_percent: parseFloat(vendor.commission_percent) || 0,
          commission_type: vendor.commission_type || "percentage",
          notes: vendor.notes || "",
        })) || [],
        discount_type: data.discount_type,
        discount_value: parseFloat(data.discount_value) || 0,
        tax: parseFloat(data.tax) || 0,
        is_taxable: data.is_taxable,
        subtotal: subtotal,
        total_amount: total,
        valid_until: data.valid_until,
        status: data.status,
        notes: data.notes || "",
      };

      console.log("Quotation Payload:", payload);

      if (editingQuotation && editingQuotation.id) {
        await quotationsApi.update(editingQuotation.id, payload);
      } else {
        await quotationsApi.create(payload);
      }
      setIsModalOpen(false);
      setEditingQuotation(null);
      loadQuotations();
    } catch (error: any) {
      console.error("Error saving quotation:", error);
      const errorMessage = error?.message || "Failed to save quotation";
      console.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }


  const transformQuotationForEdit = (quotation: Quotation) => {
    // Get services from API response - items field contains quotation_services data
    const services = quotation.items || quotation.quotation_services || [];
    
    // Get vendors from API response - vendors_data field contains quotation_vendors data
    const vendors = quotation.vendors_data || quotation.quotation_vendors || [];
    
    return {
      id: quotation.id,
      quotation_number: quotation.quotation_number,
      client_id: quotation.client_id,
      client_name: quotation.client?.name || "",
      event_name: quotation.event_name || quotation.name || (quotation.client?.name && quotation.event_type ? `${quotation.client.name} - ${quotation.event_type}` : quotation.event_type) || "",
      event_type: quotation.event_type || "",
      guests: String((quotation as any).guest_count ?? quotation.guests ?? ""),
      event_date: quotation.event_date ? ((/^\d{4}-\d{2}-\d{2}$/.test(quotation.event_date)) ? quotation.event_date : (() => { const d = new Date(quotation.event_date); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()) : "",
      package_id: quotation.package_id,
      venue: quotation.venue || "",
      venue_id: quotation.venue_id,
      services_data: Array.isArray(services) ? services.map((item: any) => ({
        id: item.id,
        service_id: item.service_id,
        description: item.description || item.service_name || "",
        vendor_id: item.vendor_id,
        quantity: parseInt(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        cost_price: parseFloat(item.cost_price) || 0,
        notes: item.notes || "",
      })) : [],
      vendors_data: Array.isArray(vendors) ? vendors.map((vendor: any) => ({
        id: vendor.id,
        vendor_id: vendor.vendor_id,
        description: vendor.description || "",
        quantity: parseInt(vendor.quantity) || 1,
        unit: vendor.unit || "unit",
        unit_price: parseFloat(vendor.unit_price) || 0,
        cost_price: parseFloat(vendor.cost_price) || 0,
        commission_percent: parseFloat(vendor.commission_percent) || 0,
        commission_type: vendor.commission_type || "percentage",
        notes: vendor.notes || "",
      })) : [],
      discount_type: quotation.discount_type || "fixed",
      discount_value: String(quotation.discount_value || "0"),
      tax: quotation.tax !== undefined && quotation.tax !== null ? String(quotation.tax) : "0",
      is_taxable: (quotation as any).is_taxable !== undefined ? (quotation as any).is_taxable : true,
      notes: quotation.notes || "",
      valid_until: quotation.valid_until || "",
      status: quotation.status || "draft",
      subtotal: quotation.subtotal || 0,
      total_amount: quotation.total || 0,
    };
  };

  const columns = [
    { key: "id", header: "Quote #", render: (q: Quotation) => (
      <div>
        <div className="font-mono text-sm font-semibold text-stone-800">{q.quotation_number || `#${q.id}`}</div>
        <div className="text-xs text-stone-500 mt-0.5">{q.event_type}</div>
      </div>
    )},
    { key: "client", header: "Client", render: (q: Quotation) => (
      <div className="text-sm font-medium text-stone-700">{q.client?.name || "-"}</div>
    )},
    { key: "event_date", header: "Event Date", render: (q: Quotation) => (
      <div className="text-sm text-stone-600">{formatDate(q.event_date)}</div>
    )},
    { key: "guests", header: "Guests", align: "center" as const, render: (q: Quotation) => (
      <div className="text-sm font-medium text-stone-700">{(q as any).guest_count ?? q.guests ?? "-"}</div>
    )},
    { key: "package", header: "Package", render: (q: Quotation) => (
      <div className="text-sm text-stone-600">{q.package?.name || "-"}</div>
    )},
    { key: "subtotal", header: "Subtotal", align: "right" as const, render: (q: Quotation) => (
      <span className="text-sm text-stone-600">{formatCurrencyFull(q.subtotal)}</span>
    )},
    { key: "total", header: "Total", align: "right" as const, render: (q: Quotation) => (
      <span className="font-semibold text-amber-700">{formatCurrencyFull(q.total)}</span>
    )},
    { key: "status", header: "Status", render: (q: Quotation) => (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setStatusUpdateQuotation(q);
          setIsStatusPopupOpen(true);
        }}
        className="cursor-pointer hover:scale-105 transition-transform"
        title="Click to update status"
      >
        <Badge label={q.status} status={q.status} />
      </button>
    )},
    { key: "valid_until", header: "Valid Until", render: (q: Quotation) => (
      <span className={`text-xs font-medium ${new Date(q.valid_until) < new Date() ? 'text-rose-500' : 'text-stone-500'}`}>
        {formatDate(q.valid_until)}
      </span>
    )},
  ];

  const handleClone = (quotation: Quotation) => {
    const transformedQuotation = transformQuotationForEdit(quotation);
    const clonedQuotation: Partial<QuotationFormData> = {
      ...transformedQuotation,
      id: undefined,
      quotation_number: undefined,
      status: "draft",
      notes: quotation.notes ? `Clone of #${quotation.id}: ${quotation.notes}` : `Clone of #${quotation.id}`,
    };
    setEditingQuotation(clonedQuotation);
    setIsModalOpen(true);
  };

  const isConverted = (q: Quotation) => q.status === "converted";

  const printQuotationPDF = (quote: Quotation) => {
    setPrintQuotation(quote);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintQuotation(null), 100);
    }, 100);
  };

  const actions = (q: Quotation) => (
    <div className="flex gap-1.5 items-center">
      {/* Quick Status Actions - Workflow buttons */}
      {(q.status === "draft" || q.status === "sent") && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const nextStatus = q.status === "draft" ? "sent" : "approved";
            setStatusConfirmQuotation(q);
            setStatusConfirmNext(nextStatus);
            setStatusConfirmOpen(true);
          }}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            q.status === "draft"
              ? "bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-200"
              : "bg-emerald-100 hover:bg-emerald-200 text-emerald-700 border border-emerald-200"
          }`}
          title={q.status === "draft" ? "Mark as Sent" : "Mark as Approved"}
        >
          {q.status === "draft" ? "→ Sent" : "→ Approve"}
        </button>
      )}

      {/* Print PDF - visible for all statuses */}
      <button 
        onClick={() => printQuotationPDF(q)}
        className="p-2 rounded-lg border border-amber-200 text-amber-600 hover:bg-amber-50 transition-colors"
        title="Print PDF"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
      </button>
      {/* Edit - disabled for converted quotations */}
      <button 
        onClick={() => { 
          if (isConverted(q)) return;
          const transformedQuotation = transformQuotationForEdit(q);
          setEditingQuotation(transformedQuotation as any); 
          setIsModalOpen(true); 
        }}
        disabled={isConverted(q)}
        className={`p-2 rounded-lg border transition-colors ${
          isConverted(q)
            ? "border-stone-100 text-stone-300 cursor-not-allowed bg-stone-50"
            : "border-stone-200 text-stone-600 hover:bg-stone-50"
        }`}
        title={isConverted(q) ? "Cannot edit a converted quotation" : "Edit"}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </button>
      {/* Clone button */}
      <button 
        onClick={async () => {
          try {
            setIsCloning(q.id);
            const response = await quotationsApi.clone(q.id);
            if (response.success && response.data?.id) {
              showToast('Quotation cloned successfully', 'success');
              // Redirect to edit page of the cloned quotation
              navigate(`/quotations/detail?id=${response.data.id}&edit=true`);
            } else {
              showToast(response.message || 'Failed to clone quotation', 'error');
            }
          } catch (error: any) {
            showToast(error?.message || 'Failed to clone quotation', 'error');
          } finally {
            setIsCloning(null);
          }
        }}
        disabled={isCloning === q.id}
        className="p-2 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Clone"
      >
        {isCloning === q.id ? (
          <div className="w-4 h-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
      
      {/* Staff cannot delete quotations */}
      {!isStaff && q.status !== "approved" && q.status !== "converted" && (
        <button 
          onClick={() => { setDeletingQuotation(q); setIsDeleteConfirmOpen(true); }}
          className="p-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors"
          title="Delete"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
      {q.status === "approved" && (
        <button 
          onClick={() => handleOpenConvertModal(q)}
          className="p-2 rounded-lg border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-colors"
          title="Convert to Event"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        </button>
      )}
    </div>
  );

  const totalValue = filtered.reduce((sum, q) => sum + q.total, 0);
  const approvedValue = filtered.filter(q => q.status === "approved").reduce((sum, q) => sum + q.total, 0);

  const handleUpdateStatus = async (id: number, newStatus: string) => {
    try {
      await quotationsApi.updateStatus(id, newStatus);
      loadQuotations();
    } catch (error: any) {
      console.error("Error updating status:", error);
      throw error;
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!statusConfirmQuotation || !statusConfirmNext) return;
    
    try {
      await handleUpdateStatus(statusConfirmQuotation.id, statusConfirmNext);
      setStatusConfirmOpen(false);
      setStatusConfirmQuotation(null);
      setStatusConfirmNext("");
      showToast(`Quotation marked as ${statusConfirmNext}`, "success");
    } catch (error: any) {
      showToast(error?.message || "Failed to update status", "error");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingQuotation) return;
    
    try {
      setIsDeleting(true);
      await quotationsApi.delete(deletingQuotation.id);
      setIsDeleteConfirmOpen(false);
      setDeletingQuotation(null);
      loadQuotations();
    } catch (error: any) {
      console.error("Error deleting quotation:", error);
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col gap-4">
        {/* Status Filter - Horizontal scrollable on mobile */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 pb-2">
              {statuses.map(s => (
                <button
                  key={s}
                  onClick={() => setActiveFilter(s)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${
                    activeFilter === s
                      ? "text-white shadow-sm gold-gradient"
                      : "bg-white text-stone-500 border border-stone-200 hover:border-stone-300"
                  }`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)} ({statusCounts[s]})
                </button>
              ))}
            </div>
          </div>
          <button 
            onClick={() => { setEditingQuotation(null); setIsModalOpen(true); }}
            className="text-sm font-semibold text-white px-4 py-2 rounded-xl gold-gradient flex-shrink-0 whitespace-nowrap border border-amber-600"
          >
            + New
          </button>
        </div>
      </div>

      {/* Stats - Compact & Collapsible */}
      <div className="bg-white rounded-xl border border-stone-200">
        <button
          onClick={() => setShowStats(!showStats)}
          className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <svg className={`w-5 h-5 text-amber-600 transition-transform ${showStats ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            <span className="font-semibold text-stone-700">Quick Stats</span>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{filtered.length} quotes</span>
          </div>
          <div className="text-right">
            <div className="text-sm font-bold text-amber-700">{formatCurrencyFull(filtered.reduce((sum, q) => sum + q.total, 0))}</div>
            <div className="text-xs text-stone-500">Total Value</div>
          </div>
        </button>

        {showStats && (
          <div className="border-t border-stone-200 grid grid-cols-2 sm:grid-cols-4 gap-2 p-3">
            {[
              ["Total Quotes", filtered.length, "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", "bg-sky-50", "text-sky-700"],
              ["Draft", filtered.filter(q => q.status === 'draft').length, "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z", "bg-stone-50", "text-stone-600"],
              ["Approved", filtered.filter(q => q.status === 'approved').length, "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z", "bg-emerald-50", "text-emerald-700"],
              ["Sent", filtered.filter(q => q.status === 'sent').length, "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z", "bg-blue-50", "text-blue-700"],
            ].map(([label, count, icon, bg, text], idx) => (
              <div key={idx} className="bg-white rounded-lg p-2.5 border border-stone-200 text-center">
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center mb-1.5 mx-auto ${bg}`}>
                  <svg className={`w-3.5 h-3.5 ${text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={icon as string} />
                  </svg>
                </div>
                <div className="text-sm font-bold text-stone-800">{count as any}</div>
                <div className="text-xs text-stone-500">{label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search */}
      <SearchInput 
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search by client, quote #, or event type..."
        className="w-full sm:max-w-md"
      />

      {/* Table */}
      <DataTable
        data={filtered}
        columns={columns}
        keyExtractor={(q) => String(q.id)}
        onRowClick={(q) => navigate(`/quotations/detail?id=${q.id}`)}
        actions={actions}
        emptyMessage="No quotations found."
        searchable={false}
      />

      {/* Add/Edit Full Screen Modal */}
      <FullScreenQuotationModal
        isOpen={isModalOpen}
        initialData={editingQuotation || undefined}
        onSubmit={(data) => {
          handleFormSubmit(data);
        }}
        onCancel={() => { setIsModalOpen(false); setEditingQuotation(null); }}
      />

      {/* Convert to Event Modal */}
      <ConvertToEventModal
        isOpen={isConvertModalOpen}
        onClose={() => { setIsConvertModalOpen(false); setConvertingQuotation(null); setConvertError(""); }}
        quotation={convertingQuotation}
        onConfirm={handleConfirmConvert}
        isLoading={isConvertLoading}
        error={convertError}
      />

      {/* Status Update Popup */}
      <StatusUpdatePopup
        isOpen={isStatusPopupOpen}
        onClose={() => { setIsStatusPopupOpen(false); setStatusUpdateQuotation(null); }}
        currentStatus={statusUpdateQuotation?.status || "draft"}
        quotationId={statusUpdateQuotation?.id || 0}
        quotationNumber={statusUpdateQuotation?.quotation_number || ""}
        onUpdateStatus={handleUpdateStatus}
      />

      {/* Status Change Confirmation Modal */}
      <ConfirmationModal
        isOpen={statusConfirmOpen}
        title={`Confirm Status Change`}
        message={`Are you sure you want to mark this quotation as ${statusConfirmNext}?`}
        itemName={statusConfirmQuotation ? `${statusConfirmQuotation.quotation_number} - ${statusConfirmQuotation.client?.name}` : ""}
        confirmText={`Mark as ${statusConfirmNext}`}
        cancelText="Cancel"
        isDangerous={false}
        onConfirm={handleConfirmStatusChange}
        onCancel={() => { setStatusConfirmOpen(false); setStatusConfirmQuotation(null); setStatusConfirmNext(""); }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        title="Delete Quotation"
        message="Are you sure you want to delete this quotation? This action cannot be undone and all associated data will be permanently removed."
        itemName={deletingQuotation ? `${deletingQuotation.quotation_number} - ${deletingQuotation.client?.name}` : ""}
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setIsDeleteConfirmOpen(false); setDeletingQuotation(null); }}
      />

      {/* Printable Quotation */}
      {printQuotation && (
        <PrintableQuotation
          quotation={printQuotation}
          businessInfo={{
            name: businessName,
            logoUrl: logoUrl || undefined,
            tagline: settings?.business?.tagline,
            address: settings?.business?.address || "123 Marine Drive, Mumbai – 400001",
            phone: settings?.business?.phone || "+91-XXXX-XXXX-XX",
            email: settings?.business?.email || "info@shalimar.com",
            gstNumber: settings?.business?.gstNumber,
          }}
        />
      )}

    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    </DashboardLayout>
  );
}

export default function QuotationsPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <QuotationsContent />
    </Suspense>
  );
}
